import { useMemo } from 'react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import 'katex/contrib/mhchem/mhchem'

interface TestQuestionProps {
  questionNumber: number
  totalQuestions: number
  questionText: string
  options: string[]
  testCode?: string
  selectedAnswer?: string | null
  onSelectAnswer: (option: string) => void
  onScroll?: () => void
  difficultyLevel?: 'EASY' | 'MEDIUM' | 'HARD'
  sectionName?: string
}

const IMAGE_BASE_URL = 'https://api.myneetbuddy.com/mnb-qb-images'

/**
 * Converts #img[image_name] placeholders to actual img elements
 * URL pattern: https://api.myneetbuddy.com/mnb-qb-images/<test_code>/<image_name>
 */
function processImagePlaceholders(content: string, testCode?: string): string {
  if (!content || !testCode) return content

  return content.replace(/#img\[([^\]]+)\]/g, (_, imageName) => {
    const imageUrl = `${IMAGE_BASE_URL}/${testCode}/${imageName}`
    return `<img src="${imageUrl}" alt="${imageName}" class="max-w-full h-auto my-2 rounded-lg" />`
  })
}

/**
 * Renders HTML content with embedded LaTeX expressions
 */
function renderLatexContent(htmlContent: string): string {
  if (!htmlContent) return ''

  let result = htmlContent

  // Process display math ($$...$$)
  result = result.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error]</span>`
    }
  })

  // Process inline math ($...$)
  result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error]</span>`
    }
  })

  // Process \( ... \) inline math
  result = result.replace(/\\\(([^)]+?)\\\)/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error]</span>`
    }
  })

  // Process \[ ... \] display math
  result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error]</span>`
    }
  })

  return result
}

const OPTION_LABELS = ['A', 'B', 'C', 'D']

export default function TestQuestion({
  questionNumber,
  totalQuestions,
  questionText,
  options,
  testCode,
  selectedAnswer,
  onSelectAnswer,
  onScroll,
  difficultyLevel,
  sectionName,
}: TestQuestionProps) {
  const renderedQuestion = useMemo(
    () => renderLatexContent(processImagePlaceholders(questionText, testCode)),
    [questionText, testCode]
  )

  const renderedOptions = useMemo(
    () => options.map((opt) => renderLatexContent(processImagePlaceholders(opt, testCode))),
    [options, testCode]
  )

  return (
    <div className="flex flex-col h-full">
      {/* Question Header */}
      <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-gray-900">
              Q.{questionNumber}
            </span>
            {sectionName && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                {sectionName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {difficultyLevel && (
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${
                  difficultyLevel === 'EASY'
                    ? 'bg-green-100 text-green-700'
                    : difficultyLevel === 'MEDIUM'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {difficultyLevel}
              </span>
            )}
            <span className="text-sm text-gray-500">
              {questionNumber} / {totalQuestions}
            </span>
          </div>
        </div>
      </div>

      {/* Question Content - Scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4" onScroll={onScroll}>
        {/* Question Text */}
        <div
          className="text-gray-800 text-base leading-relaxed mb-6"
          dangerouslySetInnerHTML={{ __html: renderedQuestion }}
        />

        {/* Options */}
        <div className="space-y-3">
          {renderedOptions.map((optionHtml, idx) => {
            const optionLabel = OPTION_LABELS[idx]
            const isSelected = selectedAnswer === optionLabel

            return (
              <button
                key={idx}
                onClick={() => onSelectAnswer(optionLabel)}
                className={`w-full flex items-start p-3 rounded-lg border-2 transition-all text-left ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium mr-3 ${
                    isSelected
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {optionLabel}
                </span>
                <div
                  className="flex-1 text-gray-700 pt-1"
                  dangerouslySetInnerHTML={{ __html: optionHtml }}
                />
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
